import { IMedia } from "../../../Api/Interface/api.interface";
import { DesignerZone, WIDGET_LABELS } from "./types";

/**
 * Shared helpers for the media-backed widgets (image / video) in the
 * Template Designer. Keeping the formatting + validation here means the
 * inspector, the canvas preview and the save flow all agree on what
 * "complete" means and how metadata is displayed.
 */

/** Human-readable file size, e.g. 18.4 MB. */
export const formatBytes = (bytes?: number | null): string => {
  if (!bytes || bytes <= 0) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  const rounded = n >= 100 || i === 0 ? Math.round(n) : Math.round(n * 10) / 10;
  return `${rounded} ${units[i]}`;
};

/** mm:ss from a duration in seconds. Returns null when unknown. */
export const formatDuration = (seconds?: number | null): string | null => {
  if (seconds == null || seconds <= 0) return null;
  const s = Math.round(seconds);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
};

/** Widget types that require a selected media record before publishing. */
export const isMediaWidget = (t: string): t is "image" | "video" =>
  t === "image" || t === "video";

/** The media_id stored in a zone's widget_config, or null. */
export const zoneMediaId = (zone: DesignerZone): number | null => {
  const raw = (zone.widget_config as Record<string, unknown>)?.media_id;
  return typeof raw === "number" && Number.isFinite(raw) ? raw : null;
};

/** A media zone with no usable source — cannot be published. A video zone is
 *  complete either with a library media_id OR a direct http(s) video URL. */
export const zoneIsIncomplete = (zone: DesignerZone): boolean => {
  if (!isMediaWidget(zone.widget_type)) return false;
  const cfg = (zone.widget_config as Record<string, unknown>) ?? {};
  if (zone.widget_type === "video" && cfg.source === "url") {
    const url = typeof cfg.video_url === "string" ? cfg.video_url.trim() : "";
    return !/^https?:\/\/\S+/i.test(url);
  }
  return zoneMediaId(zone) == null;
};

export interface ZoneProblem {
  uid: string;
  label: string;
  message: string;
}

/**
 * Validate the media-backed zones. Phase 1 covers image/video required
 * media only; later phases extend this to website/qr/menu_board.
 */
export const validateZones = (zones: DesignerZone[]): ZoneProblem[] => {
  const problems: ZoneProblem[] = [];
  zones.forEach((z) => {
    if (zoneIsIncomplete(z)) {
      const cfg = (z.widget_config as Record<string, unknown>) ?? {};
      const message =
        z.widget_type === "image" ? "select an image"
        : cfg.source === "url" ? "enter a valid video URL"
        : "select a video or enter a video URL";
      problems.push({
        uid: z._uid,
        label: z.name || WIDGET_LABELS[z.widget_type],
        message,
      });
    }
  });
  return problems;
};

/** Build a fast id → media lookup from the library list. */
export const indexMedia = (list: IMedia[]): Record<number, IMedia> => {
  const map: Record<number, IMedia> = {};
  list.forEach((m) => {
    map[m.id] = m;
  });
  return map;
};
